/**
 * SyntheseTab — Onglet Synthese (8e onglet)
 *
 * Recapitule les elements cles du theme natal selon les 5 temps
 * de la 2eme lecture (methode astrologie humaniste evolutive).
 *
 * Tableau de bord de lecture — pas de generation de texte automatique.
 */

import { useState } from 'react';
import { colors } from '../../styles/tokens.js';
import type { ChartAnalysis, PlanetPositionData } from '../../types/clients.js';

/** Maitrise moderne des signes (conforme au brief) */
const MAITRISE_MODERNE: Record<string, string> = {
  'Bélier': 'Mars', 'Taureau': 'Vénus', 'Gémeaux': 'Mercure',
  'Cancer': 'Lune', 'Lion': 'Soleil', 'Vierge': 'Mercure',
  'Balance': 'Vénus', 'Scorpion': 'Pluton', 'Sagittaire': 'Jupiter',
  'Capricorne': 'Saturne', 'Verseau': 'Uranus', 'Poissons': 'Neptune',
};

/** Types planetaires — descriptions courtes */
const TYPES_PLANETAIRES: Record<string, string> = {
  'Soleil': "Personnalité solaire : besoin de reconnaissance et d'expression créative.",
  'Lune': "Personnalité lunaire : réceptive et accueillante, besoin de sérénité.",
  'Mercure': "Personnalité mercurienne : curiosité intellectuelle, besoin de communiquer.",
  'Vénus': "Personnalité vénusienne : sociable, sensible à la beauté et à l'harmonie.",
  'Mars': "Personnalité martienne : dynamique, impatient d'agir, combatif.",
  'Jupiter': "Personnalité jupitérienne : optimiste, expansif, qualités de meneur.",
  'Saturne': "Personnalité saturnienne : patiente, prudente, philosophe.",
  'Uranus': "Personnalité uranienne : idéaliste, visionnaire, affirme sa singularité.",
  'Neptune': "Personnalité neptunienne : intuitive, branchée sur les réalités invisibles.",
  'Pluton': "Personnalité plutonienne : lucide, intuitions pénétrantes, besoin de pouvoir.",
};

/** Symboles des planetes */
const SYMBOLES: Record<string, string> = {
  'Soleil': '☉', 'Lune': '☽', 'Mercure': '☿', 'Vénus': '♀', 'Mars': '♂',
  'Jupiter': '♃', 'Saturne': '♄', 'Uranus': '♅', 'Neptune': '♆', 'Pluton': '♇',
  'Ascendant': 'AS', 'AS': 'AS',
};

/** Aspects significatifs (conjonction, carre, trigone, opposition) */
const ASPECTS_SIGNIFICATIFS = new Set(['Conjonction', 'Carré', 'Trigone', 'Opposition']);
/** Aspects de tension */
const ASPECTS_TENSION = new Set(['Carré', 'Opposition', 'Quinconce']);

interface Props {
  analysis: ChartAnalysis;
}

export function SyntheseTab({ analysis }: Props) {
  const [showHelp, setShowHelp] = useState(false);

  const planetes = analysis.planetes ?? [];
  const signature = analysis.signature;
  const structure = analysis.structureSpatiale;
  const aspects = analysis.aspects?.aspects ?? [];
  const configurations = analysis.configurations ?? [];
  const dominantesSelectionnees = analysis.dominantesSelectionnees ?? [];
  const dominantes = analysis.dominantes ?? [];
  const axeNodal = analysis.axeNodal;
  const dessin = analysis.dessinPlanetaire;

  /** Trouve une planete par nom */
  function trouverPlanete(nom: string): PlanetPositionData | undefined {
    return planetes.find((p) => p.nom === nom);
  }

  /** Trouve le maitre de l'AS */
  function getMaitreAS(): { nom: string; position: PlanetPositionData | undefined } | null {
    const as = planetes.find((p) => p.nom === 'Ascendant' || p.nom === 'AS');
    if (!as) return null;
    const maitre = MAITRISE_MODERNE[as.signe];
    if (!maitre) return null;
    return { nom: maitre, position: trouverPlanete(maitre) };
  }

  /** Trouve le maitre du Soleil */
  function getMaitreSoleil(): { nom: string; position: PlanetPositionData | undefined } | null {
    const soleil = trouverPlanete('Soleil');
    if (!soleil) return null;
    const maitre = MAITRISE_MODERNE[soleil.signe];
    if (!maitre) return null;
    return { nom: maitre, position: trouverPlanete(maitre) };
  }

  /** Aspects significatifs recus par une planete */
  function aspectsSignificatifs(nomPlanete: string) {
    return aspects.filter((a) =>
      ASPECTS_SIGNIFICATIFS.has(a.type) &&
      (a.planete1 === nomPlanete || a.planete2 === nomPlanete),
    );
  }

  /** Aspects de tension recus par une planete */
  function aspectsTension(nomPlanete: string) {
    return aspects.filter((a) =>
      ASPECTS_TENSION.has(a.type) &&
      (a.planete1 === nomPlanete || a.planete2 === nomPlanete),
    );
  }

  /** Badge aspect */
  function AspectBadge({ type, planete, orbe }: { type: string; planete: string; orbe: number }) {
    const couleurs: Record<string, { bg: string; text: string }> = {
      'Conjonction': { bg: '#dbeafe', text: '#1e40af' },
      'Trigone': { bg: '#d1fae5', text: '#065f46' },
      'Carré': { bg: '#fee2e2', text: '#991b1b' },
      'Opposition': { bg: '#fecaca', text: '#7f1d1d' },
      'Quinconce': { bg: '#fef3c7', text: '#92400e' },
    };
    const c = couleurs[type] ?? { bg: '#f3f4f6', text: '#374151' };
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        fontSize: '0.75rem', padding: '1px 8px', borderRadius: 10,
        background: c.bg, color: c.text, whiteSpace: 'nowrap',
      }}>
        {type} {planete} ({orbe.toFixed(1)}°)
      </span>
    );
  }

  /** Affiche signe + maison d'une planete */
  function PositionBadge({ planete }: { planete: PlanetPositionData }) {
    return (
      <span style={{ fontSize: '0.85rem', color: colors.text }}>
        <strong>{planete.signe}</strong>
        <span style={{ color: colors.textMid }}> — M.{planete.maison}</span>
        {planete.retrograde && (
          <span style={{ fontSize: '0.75rem', color: '#dc2626', marginLeft: 4 }}>℞</span>
        )}
      </span>
    );
  }

  /** Ligne de planete avec aspects */
  function PlaneteLigne({ nom, signification }: { nom: string; signification: string }) {
    const p = trouverPlanete(nom);
    if (!p && nom !== 'Maître AS' && nom !== 'Maître ☉') return null;

    // Cas speciaux pour Maitre AS / Maitre Soleil
    let planeteData = p;
    let label = `${SYMBOLES[nom] ?? ''} ${nom}`;

    if (nom === 'Maître AS') {
      const maitre = getMaitreAS();
      if (!maitre?.position) return null;
      planeteData = maitre.position;
      label = `${SYMBOLES[maitre.nom] ?? ''} ${maitre.nom} (maître AS)`;
    }
    if (nom === 'Maître ☉') {
      const maitre = getMaitreSoleil();
      if (!maitre?.position) return null;
      planeteData = maitre.position;
      label = `${SYMBOLES[maitre.nom] ?? ''} ${maitre.nom} (maître ☉)`;
    }

    if (!planeteData) return null;

    const asp = aspectsSignificatifs(planeteData.nom);

    return (
      <div style={{
        display: 'grid', gridTemplateColumns: '180px 140px 1fr',
        alignItems: 'center', gap: 8, padding: '6px 0',
        borderBottom: '1px solid #f3f4f6', fontSize: '0.85rem',
      }}>
        <div>
          <span style={{ fontWeight: 600, color: colors.text }}>{label}</span>
          <div style={{ fontSize: '0.75rem', color: colors.textMid, fontStyle: 'italic' }}>{signification}</div>
        </div>
        <PositionBadge planete={planeteData} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {asp.map((a, i) => {
            const autre = a.planete1 === planeteData!.nom ? a.planete2 : a.planete1;
            return <AspectBadge key={i} type={a.type} planete={autre} orbe={a.orbe} />;
          })}
        </div>
      </div>
    );
  }

  // ── Calculs pour le 1er temps ──
  const elements = signature?.elements ?? {};
  const modes = signature?.modes ?? {};
  const polarites = signature?.polarites ?? {};

  // Element dominant et en carence
  const elemEntries = Object.entries(elements).sort((a, b) => b[1].weight - a[1].weight);
  const elemDominant = elemEntries[0];
  const elemCarence = elemEntries.find(([, v]) => v.weight <= 2);

  // Mode dominant
  const modeEntries = Object.entries(modes).sort((a, b) => b[1].weight - a[1].weight);
  const modeDominant = modeEntries[0];

  // Polarite dominante
  const polEntries = Object.entries(polarites).sort((a, b) => b[1].percentage - a[1].percentage);
  const polDominante = polEntries[0];

  // Hemispheres
  const hemi = structure?.hemispheres;

  // Signes et maisons tres occupes (>= 3 planetes)
  // Filtrer : 10 planetes classiques uniquement (pas de points fictifs)
  const PLANETES_CLASSIQUES = new Set([
    'Soleil', 'Lune', 'Mercure', 'Vénus', 'Mars',
    'Jupiter', 'Saturne', 'Uranus', 'Neptune', 'Pluton',
    'Ascendant', 'MC',
  ]);
  const signesCounts = new Map<string, string[]>();
  const maisonsCounts = new Map<number, string[]>();
  for (const p of planetes) {
    if (!PLANETES_CLASSIQUES.has(p.nom)) continue;
    if (!signesCounts.has(p.signe)) signesCounts.set(p.signe, []);
    signesCounts.get(p.signe)!.push(p.nom);
    if (!maisonsCounts.has(p.maison)) maisonsCounts.set(p.maison, []);
    maisonsCounts.get(p.maison)!.push(p.nom);
  }
  const signesOccupes = [...signesCounts.entries()].filter(([, ps]) => ps.length >= 3);
  const maisonsOccupees = [...maisonsCounts.entries()].filter(([, ps]) => ps.length >= 3);

  // Dominantes retenues
  const dominantesRetenues = dominantes.filter((d) => dominantesSelectionnees.includes(d.planete));

  // Configurations majeures
  const configsMajeures = configurations.filter((c) => c.importance === 'majeure');

  return (
    <div>
      {/* En-tete + Aide */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '1rem',
      }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', color: colors.text, fontWeight: 700 }}>
          <i className="bi bi-journal-text" style={{ color: colors.accent, marginRight: 8 }} />
          Synthese — Les 5 temps de la lecture
        </h3>
        <button
          onClick={() => setShowHelp(!showHelp)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '5px 12px', fontSize: '0.8rem', fontWeight: 500,
            background: showHelp ? colors.accent : '#f3f4f6',
            color: showHelp ? '#fff' : colors.textMid,
            border: 'none', borderRadius: 6, cursor: 'pointer',
            transition: 'all 0.2s', fontFamily: 'inherit',
          }}
        >
          <i className="bi bi-question-circle" />
          Aide
        </button>
      </div>

      {showHelp && (
        <div style={{
          background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
          padding: '1.25rem', marginBottom: '1.25rem', fontSize: '0.85rem',
          lineHeight: 1.7, color: '#1e3a5f',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0c4a6e' }}>
              Les 5 temps de la 2eme lecture
            </h4>
            <button onClick={() => setShowHelp(false)} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '1.2rem', color: '#64748b', lineHeight: 1,
            }}>&times;</button>
          </div>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li><strong>Regard global</strong> — signature energetique, dessin planetaire, repartition spatiale</li>
            <li><strong>Dominantes</strong> — planetes dominantes, configurations majeures, signes/maisons occupes</li>
            <li><strong>Fondements de la personnalite</strong> — Soleil, Lune, AS, maitre AS, Mercure, Venus, Mars</li>
            <li><strong>Freins et part d'ombre</strong> — Saturne, Pluton, Neptune (aspects de tension)</li>
            <li><strong>Chemin d'epanouissement</strong> — Soleil, maitre du Soleil, Jupiter, axe nodal</li>
          </ol>
          <p style={{ margin: '0.5rem 0 0', fontStyle: 'italic' }}>
            Cet onglet regroupe les donnees deja calculees dans les autres onglets,
            reorganisees selon la progression pedagogique de la lecture du theme.
          </p>
        </div>
      )}

      {/* ═══════ 1er TEMPS — Regard global ═══════ */}
      <Section titre="1er Temps — Regard global" icon="bi-eye" couleur="#0369a1">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {elemDominant && (
            <Pill bg="#dbeafe" color="#1e40af">
              Element dominant : <strong>{elemDominant[0]}</strong> ({elemDominant[1].weight} pts)
            </Pill>
          )}
          {elemCarence && (
            <Pill bg="#fee2e2" color="#991b1b">
              Carence : <strong>{elemCarence[0]}</strong> ({elemCarence[1].weight} pts)
            </Pill>
          )}
          {polDominante && (
            <Pill bg="#f3e8ff" color="#6b21a8">
              Polarite : <strong>{polDominante[0]}</strong> ({polDominante[1].percentage}%)
            </Pill>
          )}
          {modeDominant && (
            <Pill bg="#fef3c7" color="#92400e">
              Mode : <strong>{modeDominant[0]}</strong> ({modeDominant[1].weight} pts)
            </Pill>
          )}
        </div>

        {dessin && (
          <div style={{ fontSize: '0.85rem', marginBottom: 8, color: colors.text }}>
            <i className="bi bi-diagram-3" style={{ marginRight: 6, color: '#0ea5e9' }} />
            Dessin planetaire : <strong>{dessin.type}</strong>
            {dessin.description && (
              <span style={{ color: colors.textMid }}> — {dessin.description}</span>
            )}
          </div>
        )}

        {hemi && (
          <div style={{ fontSize: '0.85rem', color: colors.textMid, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            <span>Est {hemi.Est.percentage}% / Ouest {hemi.Ouest.percentage}%</span>
            <span>Nord {hemi.Nord.percentage}% / Sud {hemi.Sud.percentage}%</span>
          </div>
        )}
      </Section>

      {/* ═══════ 2eme TEMPS — Dominantes ═══════ */}
      <Section titre="2eme Temps — Dominantes" icon="bi-star-fill" couleur="#b45309">
        {dominantesRetenues.length > 0 ? (
          <div style={{ marginBottom: 12 }}>
            {dominantesRetenues.map((d) => (
              <div key={d.planete} style={{
                background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8,
                padding: '0.6rem 1rem', marginBottom: 6,
              }}>
                <div style={{ fontWeight: 700, color: '#92400e', fontSize: '0.9rem' }}>
                  {SYMBOLES[d.planete] ?? ''} {d.planete}
                  {d.score > 0 && (
                    <span style={{ fontWeight: 400, fontSize: '0.8rem', marginLeft: 8, color: '#b45309' }}>
                      ({d.score} critere{d.score > 1 ? 's' : ''})
                    </span>
                  )}
                </div>
                {d.criteres && d.criteres.length > 0 && (
                  <div style={{ fontSize: '0.8rem', color: '#78350f', marginTop: 2 }}>
                    {d.criteres.join(' · ')}
                  </div>
                )}
                {TYPES_PLANETAIRES[d.planete] && (
                  <div style={{ fontSize: '0.8rem', color: '#a16207', fontStyle: 'italic', marginTop: 2 }}>
                    {TYPES_PLANETAIRES[d.planete]}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '0.85rem', color: colors.textMuted, fontStyle: 'italic', marginBottom: 12 }}>
            Aucune dominante selectionnee — cocher dans l'onglet Positions.
          </div>
        )}

        {configsMajeures.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: colors.textMid, marginBottom: 4 }}>
              Configurations majeures :
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {configsMajeures.map((c, i) => (
                <Pill key={i} bg="#ede9fe" color="#5b21b6">
                  {c.type}{c.planetes && c.planetes.length > 0 ? ` (${c.planetes.join(', ')})` : ''}
                </Pill>
              ))}
            </div>
          </div>
        )}

        {(signesOccupes.length > 0 || maisonsOccupees.length > 0) && (
          <div style={{ fontSize: '0.85rem', color: colors.text }}>
            {signesOccupes.map(([signe, ps]) => (
              <div key={signe} style={{ marginBottom: 2 }}>
                <strong>{signe}</strong> — {ps.length} planetes : {ps.join(', ')}
              </div>
            ))}
            {maisonsOccupees.map(([maison, ps]) => (
              <div key={maison} style={{ marginBottom: 2 }}>
                <strong>Maison {maison}</strong> — {ps.length} planetes : {ps.join(', ')}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ═══════ 3eme TEMPS — Fondements de la personnalite ═══════ */}
      <Section titre="3eme Temps — Fondements de la personnalite" icon="bi-person-fill" couleur="#0f766e">
        <div style={{ borderTop: '1px solid #e5e7eb' }}>
          <PlaneteLigne nom="Soleil" signification="Identite profonde" />
          <PlaneteLigne nom="Lune" signification="Besoins emotionnels" />
          <PlaneteLigne nom="Ascendant" signification="Personnalite visible" />
          <PlaneteLigne nom="Maître AS" signification="Expression de la personnalite" />
          <PlaneteLigne nom="Mercure" signification="Intelligence, communication" />
          <PlaneteLigne nom="Vénus" signification="Puissance affective, valeurs" />
          <PlaneteLigne nom="Mars" signification="Mode d'action" />
        </div>
      </Section>

      {/* ═══════ 4eme TEMPS — Freins & Part d'ombre ═══════ */}
      <Section titre="4eme Temps — Freins et part d'ombre" icon="bi-shield-exclamation" couleur="#be123c">
        {(['Saturne', 'Pluton'] as const).map((nom) => {
          const p = trouverPlanete(nom);
          if (!p) return null;
          const tensions = aspectsTension(nom);
          return (
            <div key={nom} style={{
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
              padding: '0.6rem 1rem', marginBottom: 8,
            }}>
              <div style={{ fontWeight: 700, color: '#991b1b', fontSize: '0.9rem' }}>
                {SYMBOLES[nom]} {nom}
                <span style={{ fontWeight: 400, marginLeft: 8 }}>
                  <PositionBadge planete={p} />
                </span>
              </div>
              {tensions.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                  {tensions.map((a, i) => {
                    const autre = a.planete1 === nom ? a.planete2 : a.planete1;
                    return <AspectBadge key={i} type={a.type} planete={autre} orbe={a.orbe} />;
                  })}
                </div>
              )}
              {tensions.length === 0 && (
                <div style={{ fontSize: '0.8rem', color: '#b91c1c', fontStyle: 'italic', marginTop: 2 }}>
                  Pas d'aspect de tension majeur
                </div>
              )}
            </div>
          );
        })}

        {/* Neptune : uniquement si aspect de tension */}
        {(() => {
          const neptune = trouverPlanete('Neptune');
          if (!neptune) return null;
          const tensions = aspectsTension('Neptune');
          if (tensions.length === 0) return null;
          return (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
              padding: '0.6rem 1rem', marginBottom: 8,
            }}>
              <div style={{ fontWeight: 700, color: '#991b1b', fontSize: '0.9rem' }}>
                {SYMBOLES['Neptune']} Neptune
                <span style={{ fontWeight: 400, marginLeft: 8 }}>
                  <PositionBadge planete={neptune} />
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                {tensions.map((a, i) => {
                  const autre = a.planete1 === 'Neptune' ? a.planete2 : a.planete1;
                  return <AspectBadge key={i} type={a.type} planete={autre} orbe={a.orbe} />;
                })}
              </div>
            </div>
          );
        })()}
      </Section>

      {/* ═══════ 5eme TEMPS — Chemin d'epanouissement ═══════ */}
      <Section titre="5eme Temps — Chemin d'epanouissement" icon="bi-sun-fill" couleur="#b45309">
        {/* Soleil */}
        {(() => {
          const soleil = trouverPlanete('Soleil');
          if (!soleil) return null;
          return (
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
              padding: '0.6rem 1rem', marginBottom: 8,
            }}>
              <div style={{ fontWeight: 700, color: '#92400e', fontSize: '0.9rem' }}>
                ☉ Soleil — But spirituel
              </div>
              <div style={{ fontSize: '0.85rem', color: '#78350f', marginTop: 2 }}>
                <PositionBadge planete={soleil} />
              </div>
            </div>
          );
        })()}

        {/* Maitre du Soleil */}
        {(() => {
          const maitre = getMaitreSoleil();
          if (!maitre?.position) return null;
          const asp = aspectsSignificatifs(maitre.position.nom);
          return (
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
              padding: '0.6rem 1rem', marginBottom: 8,
            }}>
              <div style={{ fontWeight: 700, color: '#92400e', fontSize: '0.9rem' }}>
                {SYMBOLES[maitre.nom] ?? ''} {maitre.nom} — Maitre du Soleil
              </div>
              <div style={{ fontSize: '0.85rem', color: '#78350f', marginTop: 2 }}>
                <PositionBadge planete={maitre.position} />
              </div>
              {asp.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                  {asp.map((a, i) => {
                    const autre = a.planete1 === maitre.nom ? a.planete2 : a.planete1;
                    return <AspectBadge key={i} type={a.type} planete={autre} orbe={a.orbe} />;
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* Jupiter */}
        {(() => {
          const jupiter = trouverPlanete('Jupiter');
          if (!jupiter) return null;
          return (
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
              padding: '0.6rem 1rem', marginBottom: 8,
            }}>
              <div style={{ fontWeight: 700, color: '#92400e', fontSize: '0.9rem' }}>
                ♃ Jupiter — Expansion et epanouissement
              </div>
              <div style={{ fontSize: '0.85rem', color: '#78350f', marginTop: 2 }}>
                <PositionBadge planete={jupiter} />
              </div>
            </div>
          );
        })()}

        {/* Dominante planetaire — rappel */}
        {dominantesRetenues.length > 0 && (
          <div style={{
            background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
            padding: '0.6rem 1rem', marginBottom: 8,
          }}>
            <div style={{ fontWeight: 700, color: '#92400e', fontSize: '0.9rem' }}>
              Dominante{dominantesRetenues.length > 1 ? 's' : ''} retenue{dominantesRetenues.length > 1 ? 's' : ''}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#78350f', marginTop: 2 }}>
              {dominantesRetenues.map((d) => `${SYMBOLES[d.planete] ?? ''} ${d.planete}`).join(' · ')}
            </div>
          </div>
        )}

        {/* Axe Nodal */}
        {axeNodal && (axeNodal.nord || axeNodal.sud) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {axeNodal.nord && (
              <div style={{
                background: '#fffbeb', border: '2px solid #f59e0b', borderRadius: 8,
                padding: '0.6rem 1rem',
              }}>
                <div style={{ fontWeight: 700, color: '#92400e', fontSize: '0.85rem' }}>
                  Noeud Nord ☊
                </div>
                <div style={{ fontSize: '0.85rem', color: '#78350f', marginTop: 2 }}>
                  <strong>{axeNodal.nord.signe}</strong> — M.{axeNodal.nord.maison}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#a16207', fontStyle: 'italic', marginTop: 2 }}>
                  Ce qui est a developper
                </div>
              </div>
            )}
            {axeNodal.sud && (
              <div style={{
                background: '#f5f5f4', border: '1px solid #d6d3d1', borderRadius: 8,
                padding: '0.6rem 1rem',
              }}>
                <div style={{ fontWeight: 700, color: '#57534e', fontSize: '0.85rem' }}>
                  Noeud Sud ☋
                </div>
                <div style={{ fontSize: '0.85rem', color: '#78716c', marginTop: 2 }}>
                  <strong>{axeNodal.sud.signe}</strong> — M.{axeNodal.sud.maison}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#a8a29e', fontStyle: 'italic', marginTop: 2 }}>
                  Ce qui est a depasser
                </div>
              </div>
            )}
          </div>
        )}
      </Section>
    </div>
  );
}

// ── Composants utilitaires ──

function Section({ titre, icon, couleur, children }: {
  titre: string; icon: string; couleur: string; children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <h4 style={{
        margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: 700,
        color: couleur, display: 'flex', alignItems: 'center', gap: 8,
        paddingBottom: 6, borderBottom: `2px solid ${couleur}20`,
      }}>
        <i className={icon} style={{ fontSize: '1rem' }} />
        {titre}
      </h4>
      {children}
    </div>
  );
}

function Pill({ bg, color, children }: { bg: string; color: string; children: React.ReactNode }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: '0.8rem', padding: '3px 10px', borderRadius: 12,
      background: bg, color, whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}
